import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentList, readFileAsBase64 } from './attachments';

export function StartDiscussionDialog({ open, onOpenChange, reportId, visualId, dataContext, screenshotData, reportState, onCreated, defaultQuestion }) {
  const [question, setQuestion] = useState('');
  const [title, setTitle] = useState('');
  const [assignTo, setAssignTo] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState({});
  const [notifyVia, setNotifyVia] = useState([]);
  const [archiveAfter, setArchiveAfter] = useState('30');
  const [attachments, setAttachments] = useState([]);
  const [dtype, setDtype] = useState('question');
  const [priority, setPriority] = useState('medium');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      api.getMentionableUsers().then(setUsers).catch(() => {});
      api.getNotificationChannels().then(setChannels).catch(() => {});
      api.getDiscussionTags().then(r => setTagSuggestions(r?.tags || [])).catch(() => {});
      setQuestion(defaultQuestion || '');
      setTitle('');
      setAssignTo([]);
      setMentions([]);
      setNotifyVia([]);
      setArchiveAfter('30');
      setAttachments([]);
      setDtype('question');
      setPriority('medium');
      setTags([]);
      setTagInput('');
    }
  }, [open, defaultQuestion]);

  const addTag = (t) => {
    const v = (t || '').trim().toLowerCase();
    if (v && !tags.includes(v)) setTags(prev => [...prev, v]);
    setTagInput('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  // Inline @mention autocomplete — matches a trailing "@partial" as the user types.
  const mentionMatch = /@([\w.\-]*)$/.exec(question);
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionSuggestions = mentionQuery === null ? [] : users.filter(u => {
    const s = `${u.name || ''} ${u.email || ''}`.toLowerCase();
    return s.includes(mentionQuery);
  }).slice(0, 6);

  const selectMention = (u) => {
    const handle = (u.name || u.email || u.user_id).replace(/\s+/g, '');
    setQuestion(prev => prev.replace(/@([\w.\-]*)$/, `@${handle} `));
    setMentions(prev => prev.includes(u.user_id) ? prev : [...prev, u.user_id]);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB limit`); continue; }
      const dataUrl = await readFileAsBase64(file);
      setAttachments(prev => [...prev, { name: file.name, size: file.size, type: file.type, data_url: dataUrl }]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleNotify = (ch) => setNotifyVia(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);

  const handleCreate = async () => {
    if (!question.trim()) return;
    setSaving(true);
    try {
      const disc = await api.createDiscussion({
        report_id: reportId,
        visual_id: visualId || '',
        data_context: dataContext || {},
        report_state: reportState || {},
        screenshot: screenshotData || '',
        title,
        question,
        type: dtype,
        priority,
        tags,
        assigned_to: assignTo,
        mentions,
        notify_channels: notifyVia,
        archive_after_days: Number(archiveAfter),
        attachments: attachments.map(a => ({ name: a.name, size: a.size, type: a.type, data_url: a.data_url })),
      });
      toast.success('Discussion started' + (notifyVia.length ? ` (notified via ${notifyVia.join(', ')})` : ''));
      if (onCreated) onCreated(disc);
      onOpenChange(false);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col p-4">
        <DialogHeader className="pb-1">
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4 text-primary" /> Start a Thread
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5 overflow-y-auto pr-1 flex-1 min-h-0">
          <DataContextPreview dataContext={dataContext} />
          {screenshotData && (
            <div className="rounded-xl border border-border/30 overflow-hidden max-h-24">
              <img src={screenshotData} alt="Report snapshot" className="w-full object-cover" />
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">Group Name (optional)</Label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Q3 Loss Review, Data Quality Squad"
              className="w-full mt-1 h-9 rounded-lg bg-muted/30 border border-border/40 px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary/50"
              data-testid="discussion-title-input"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Your Question or Comment</Label>
            <div className="relative">
              <textarea
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Ask a question or share an observation... use @ to mention"
                className="w-full mt-1 rounded-xl bg-muted/30 border border-border/40 p-2.5 text-sm text-foreground resize-none min-h-[56px] outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="discussion-question-input"
              />
              {mentionSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-auto rounded-lg border border-border/50 bg-popover shadow-lg p-1" data-testid="mention-autocomplete">
                  {mentionSuggestions.map(u => (
                    <button key={u.user_id} type="button" onClick={() => selectMention(u)} className="w-full text-left px-2 py-1.5 rounded text-[11px] text-foreground hover:bg-muted/50 flex items-center gap-2" data-testid={`mention-suggestion-${u.user_id}`}>
                      <span className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[8px] font-bold text-primary">{(u.name || u.email || '?')[0]?.toUpperCase()}</span>
                      {u.name || u.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleFileSelect} className="hidden" />
              <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] text-muted-foreground hover:text-foreground border border-border/30 hover:border-border/50 transition-colors" data-testid="attach-file-btn">
                <Paperclip className="w-3 h-3" /> Attach File
              </button>
              <span className="text-[8px] text-muted-foreground/70">Max 5MB per file</span>
            </div>
            <AttachmentList attachments={attachments} onRemove={i => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={dtype} onValueChange={setDtype}>
                <SelectTrigger className="mt-1 h-9 rounded-lg bg-muted/30 border-border/40 text-xs" data-testid="discussion-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="data_issue">Data Issue</SelectItem>
                  <SelectItem value="action_item">Action Item</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1 h-9 rounded-lg bg-muted/30 border-border/40 text-xs" data-testid="discussion-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="discussion-tags">
              {tags.map(t => (
                <Badge key={t} variant="secondary" className="text-[10px] cursor-pointer" onClick={() => removeTag(t)}>{t} ✕</Badge>
              ))}
            </div>
            <input
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) { e.preventDefault(); addTag(tagInput); } }}
              placeholder="Type a tag and press Enter"
              className="w-full mt-1.5 h-9 rounded-lg bg-muted/30 border border-border/40 px-3 text-xs text-foreground outline-none focus:ring-1 focus:ring-primary/50"
              data-testid="discussion-tag-input"
              list="discussion-tag-suggestions"
            />
            <datalist id="discussion-tag-suggestions">
              {tagSuggestions.map(t => <option key={t} value={t} />)}
            </datalist>
            {tagSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {tagSuggestions.filter(t => !tags.includes(t)).slice(0, 8).map(t => (
                  <button key={t} onClick={() => addTag(t)} className="text-[9px] px-2 py-0.5 rounded-full border border-border/30 text-muted-foreground hover:text-foreground hover:border-border/60" data-testid={`tag-suggestion-${t}`}>+ {t}</button>
                ))}
              </div>
            )}
          </div>

          <AssigneePicker users={users} assignTo={assignTo} setAssignTo={setAssignTo} />
          <NotifyChannels channels={channels} notifyVia={notifyVia} toggleNotify={toggleNotify} />

          <div>
            <Label className="text-xs text-muted-foreground">Auto-Archive After</Label>
            <Select value={archiveAfter} onValueChange={setArchiveAfter}>
              <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">6 months</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="0">Never</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="pt-2 mt-1 border-t border-border/30">
          <Button onClick={handleCreate} disabled={saving || !question.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="create-discussion-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Start Thread
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DataContextPreview({ dataContext }) {
  if (!dataContext || !Object.keys(dataContext).length) return null;
  const visibleEntries = Object.entries(dataContext).filter(([k]) => !k.startsWith('_'));
  return (
    <div className="p-3 rounded-xl bg-muted/20 border border-border/30 space-y-2">
      {visibleEntries.length > 0 && (
        <div>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5 font-semibold">Data Point</p>
          <div className="space-y-1">
            {visibleEntries.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                <span className="text-foreground font-semibold">{typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {dataContext._filters && Object.keys(dataContext._filters).length > 0 && (
        <div>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1 font-semibold">Active Filters</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(dataContext._filters).map(([k, v]) => (
              <Badge key={k} className="text-[8px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">{k.replace(/_/g, ' ')}: {String(v)}</Badge>
            ))}
          </div>
        </div>
      )}
      {dataContext._cross_filter && (
        <div>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1 font-semibold">Cross-Filter</p>
          <Badge className="text-[8px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">{dataContext._cross_filter}</Badge>
        </div>
      )}
      {dataContext._row_count && (
        <p className="text-[9px] text-muted-foreground">{dataContext._row_count} rows in dataset</p>
      )}
    </div>
  );
}

function AssigneePicker({ users, assignTo, setAssignTo }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">Participants (optional) — you will be the owner</Label>
      <div className="flex flex-wrap gap-1 mt-1.5 mb-1.5">
        {assignTo.map(uid => {
          const u = users.find(uu => uu.user_id === uid);
          return (
            <Badge key={uid} className="text-[9px] bg-primary/15 text-primary border-primary/20 gap-1 cursor-pointer" onClick={() => setAssignTo(prev => prev.filter(id => id !== uid))}>
              {u?.name || uid} &times;
            </Badge>
          );
        })}
      </div>
      <Select onValueChange={v => { if (v && !assignTo.includes(v)) setAssignTo(prev => [...prev, v]); }}>
        <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 text-xs h-8"><SelectValue placeholder="Select a user..." /></SelectTrigger>
        <SelectContent>
          {users.filter(u => !assignTo.includes(u.user_id)).map(u => (
            <SelectItem key={u.user_id} value={u.user_id} className="text-xs">{u.name || u.email} ({u.role})</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const NOTIFY_BTN_BASE = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] border transition-all';
const NOTIFY_BTN_ACTIVE = 'border-primary/40 bg-primary/10 text-primary';
const NOTIFY_BTN_INACTIVE = 'border-border/30 text-muted-foreground hover:border-border/50';

function NotifyChannels({ channels, notifyVia, toggleNotify }) {
  if (!channels.email && !channels.slack && !channels.teams && !channels.twilio) return null;
  const cls = (key) => `${NOTIFY_BTN_BASE} ${notifyVia.includes(key) ? NOTIFY_BTN_ACTIVE : NOTIFY_BTN_INACTIVE}`;
  return (
    <div>
      <Label className="text-xs text-muted-foreground">Notify Via</Label>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {channels.email && (
          <button onClick={() => toggleNotify('email')} className={cls('email')} data-testid="notify-email">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Email
          </button>
        )}
        {channels.slack && (
          <button onClick={() => toggleNotify('slack')} className={cls('slack')} data-testid="notify-slack">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.52-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.52V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.166 0a2.528 2.528 0 0 1 2.521 2.522v6.312zm-2.521 10.124a2.528 2.528 0 0 1 2.521 2.52A2.528 2.528 0 0 1 15.166 24a2.528 2.528 0 0 1-2.521-2.522v-2.52h2.521zm0-1.271a2.528 2.528 0 0 1-2.521-2.521 2.528 2.528 0 0 1 2.521-2.521h6.312A2.528 2.528 0 0 1 24 15.166a2.528 2.528 0 0 1-2.522 2.521h-6.312z"/></svg>
            Slack {channels.slack_mode === 'api' ? '(API)' : '(Webhook)'}
          </button>
        )}
        {channels.teams && (
          <button onClick={() => toggleNotify('teams')} className={cls('teams')} data-testid="notify-teams">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M19.35 8.04a4.47 4.47 0 0 0-.76-.07c-.29 0-.57.03-.84.08A5.48 5.48 0 0 0 13.5 4c-2.64 0-4.87 1.86-5.4 4.36A3.98 3.98 0 0 0 4 12.28c0 2.07 1.58 3.78 3.6 3.97v.02h11.8c2.21 0 4-1.79 4-4a4 4 0 0 0-4.05-4.23z"/></svg>
            Teams {channels.teams_mode === 'api' ? '(API)' : '(Webhook)'}
          </button>
        )}
        {channels.twilio && (
          <button onClick={() => toggleNotify('sms')} className={cls('sms')} data-testid="notify-sms">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            SMS (Twilio)
          </button>
        )}
      </div>
      {notifyVia.length === 0 && <p className="text-[9px] text-muted-foreground/70 mt-1">No notifications selected — discussion will be saved without sending alerts</p>}
    </div>
  );
}
